/**
 * Admin-only edit for a closed sale. Small surgical form (payment mode +
 * customer + salesperson + notes + sale date + branch) with a mandatory
 * reason. Full diff + running audit log rendered inline so operators can
 * see WHO changed WHAT WHEN on this sale — no back-office DB spelunking.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { X, Warning, ClockCounterClockwise } from "@phosphor-icons/react";

const PAYMENT_METHODS = ["cash", "upi", "card", "cheque", "bank_transfer", "finance", "mixed", "platform"];
const SALE_TYPES = ["cash", "finance", "exchange", "cash_with_exchange", "finance_with_exchange"];
const GST_TYPES = ["intra", "inter"];

function DiffPill({ change }) {
  const stringify = (v) => {
    if (v == null) return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  return (
    <div className="border border-canvas-line bg-canvas px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-ink-muted">{change.field}</div>
      <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
        <div className="text-destructive line-through break-all">{stringify(change.before)}</div>
        <div className="text-emerald-700 break-all">{stringify(change.after)}</div>
      </div>
    </div>
  );
}

export default function AdminEditSaleDialog({ sale, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    payment_method: sale?.payment_method || "cash",
    payment_lines: sale?.payment_lines || [],
    sale_date: sale?.sale_date || "",
    sale_type: sale?.sale_type || "cash",
    gst_type: sale?.gst_type || "intra",
    salesperson_name: sale?.salesperson_name || "",
    sales_agent_name: sale?.sales_agent_name || "",
    has_finance: !!sale?.has_finance,
    has_exchange: !!sale?.has_exchange,
    branch_id: sale?.branch_id || "",
    notes: sale?.notes || "",
    finance: sale?.finance || null,
  }));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [branches, setBranches] = useState([]);
  const [financiers, setFinanciers] = useState([]);

  const loadHistory = useCallback(async () => {
    if (!sale?.id) return;
    try {
      const { data } = await api.get(`/sales/${sale.id}/edit-audit`);
      setHistory(data || []);
    } catch (e) {
      // audit read is nice-to-have — don't block editing on failure
      setHistory([]);
    }
  }, [sale?.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
    api.get("/financiers", { params: { active_only: true } })
      .then((r) => setFinanciers(r.data || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Patch nested finance-details fields without wiping other keys.
  const setFinance = (k, v) => setForm((f) => ({
    ...f,
    finance: { ...(f.finance || {}), [k]: v },
  }));

  // When admin picks a financier from the dropdown, auto-fill the name so
  // the label saved to the sale matches the master row.
  const pickFinancier = (id) => {
    const row = financiers.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      finance: { ...(f.finance || {}), financier_id: id, financier_name: row?.name || "" },
    }));
  };

  // Per-tender-line edit. Shallow-clones the array so we don't mutate the
  // frozen `sale.payment_lines` reference, then patches the single line.
  const setLine = (idx, field, value) => {
    setForm((f) => {
      const next = (f.payment_lines || []).map((l, i) => (i === idx ? { ...l, [field]: value } : l));
      return { ...f, payment_lines: next };
    });
  };

  // Only send fields that actually changed — avoids overwriting server-
  // computed defaults with stale UI values.
  const changed = useMemo(() => {
    const diff = {};
    Object.entries(form).forEach(([k, v]) => {
      const orig = sale?.[k];
      const same = JSON.stringify(orig ?? null) === JSON.stringify(v ?? null);
      if (!same) diff[k] = v;
    });
    return diff;
  }, [form, sale]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    if (Object.keys(changed).length === 0) {
      toast.error("No fields changed");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/sales/${sale.id}/admin-edit`, {
        updates: changed,
        reason: reason.trim(),
      });
      toast.success("Sale updated");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!sale) return null;

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="admin-edit-sale-modal">
      <form onSubmit={submit} className="bg-canvas border border-canvas-line max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-canvas border-b border-canvas-line px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Admin Edit · Sale</div>
            <div className="text-lg font-semibold">{sale.invoice_no || sale.id?.slice(0, 8)?.toUpperCase()}</div>
            <div className="font-mono text-[10px] text-ink-muted">
              {sale.customer?.name} · {sale.vehicle?.vin || "—"}
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="admin-edit-close" className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2">
            <Warning size={14} weight="fill" className="text-amber-600 mt-0.5" />
            <div>
              Locked from editing here: <b>price</b>, <b>discount</b>, <b>VIN / vehicle</b>, <b>packages</b>, <b>approval status</b>. Those need reject-and-recreate to keep ledgers clean. For a VIN swap, use &ldquo;Swap Vehicle (pre-RTO)&rdquo; instead.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Payment method</span>
              <select value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="admin-edit-pm" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm uppercase">
                {PAYMENT_METHODS.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Sale type</span>
              <select value={form.sale_type} onChange={(e) => set("sale_type", e.target.value)} data-testid="admin-edit-st" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                {SALE_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase().replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Sale date</span>
              <input type="date" value={form.sale_date?.slice(0, 10) || ""} onChange={(e) => set("sale_date", e.target.value)} data-testid="admin-edit-date" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">GST type</span>
              <select value={form.gst_type} onChange={(e) => set("gst_type", e.target.value)} data-testid="admin-edit-gst" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                {GST_TYPES.map((g) => <option key={g} value={g}>{g.toUpperCase()}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Salesperson name</span>
              <input value={form.salesperson_name} onChange={(e) => set("salesperson_name", e.target.value)} data-testid="admin-edit-sp" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Sales agent name</span>
              <input value={form.sales_agent_name} onChange={(e) => set("sales_agent_name", e.target.value)} data-testid="admin-edit-sa" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
            </label>
            <label className="block col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Branch</span>
              <select value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)} data-testid="admin-edit-branch" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.has_finance} onChange={(e) => set("has_finance", e.target.checked)} data-testid="admin-edit-fin" />
              <span>Has finance</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.has_exchange} onChange={(e) => set("has_exchange", e.target.checked)} data-testid="admin-edit-exc" />
              <span>Has exchange</span>
            </label>
          </div>

          {/* Finance details — only visible when Has finance is on.
              Admin can add/update financier, DO#, loan amount, type, commission
              and mark bank-receipt directly from this dialog. */}
          {form.has_finance && (
            <div className="border border-canvas-line" data-testid="admin-edit-finance-block">
              <div className="px-3 py-2 border-b border-canvas-line bg-canvas-panel/50 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                  Finance Details
                </span>
                {form.finance?.status && (
                  <span className={`text-[9px] font-mono uppercase px-2 py-0.5 border ${
                    form.finance.status === "received" ? "border-emerald-500 text-emerald-700 bg-emerald-50" :
                    form.finance.status === "short" ? "border-amber-500 text-amber-700 bg-amber-50" :
                    "border-canvas-line text-ink-muted"
                  }`}>
                    {form.finance.status}
                  </span>
                )}
              </div>
              <div className="p-3 grid grid-cols-2 gap-3">
                <label className="block col-span-2">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Financier</span>
                  <select
                    value={form.finance?.financier_id || ""}
                    onChange={(e) => pickFinancier(e.target.value)}
                    data-testid="admin-edit-financier"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                  >
                    <option value="">Select financier…</option>
                    {financiers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Finance type</span>
                  <select
                    value={form.finance?.finance_type || "loan"}
                    onChange={(e) => setFinance("finance_type", e.target.value)}
                    data-testid="admin-edit-finance-type"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm uppercase"
                  >
                    <option value="loan">LOAN</option>
                    <option value="lien">LIEN</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">DO number</span>
                  <input
                    value={form.finance?.do_number || ""}
                    onChange={(e) => setFinance("do_number", e.target.value)}
                    placeholder="e.g. DO/2026/1245"
                    data-testid="admin-edit-do-number"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Finance amount ₹</span>
                  <input
                    type="number" step="0.01"
                    value={form.finance?.finance_amount ?? ""}
                    onChange={(e) => setFinance("finance_amount", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                    data-testid="admin-edit-finance-amount"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm tabular-nums"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Commission ₹</span>
                  <input
                    type="number" step="0.01"
                    value={form.finance?.commission_amount ?? ""}
                    onChange={(e) => setFinance("commission_amount", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                    data-testid="admin-edit-commission"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm tabular-nums"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Received ₹ (bank)</span>
                  <input
                    type="number" step="0.01"
                    value={form.finance?.amount_received ?? ""}
                    onChange={(e) => setFinance("amount_received", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                    data-testid="admin-edit-amount-received"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm tabular-nums"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Received on</span>
                  <input
                    type="date"
                    value={(form.finance?.received_on || "").slice(0, 10)}
                    onChange={(e) => setFinance("received_on", e.target.value)}
                    data-testid="admin-edit-received-on"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
                  />
                </label>
                <label className="block col-span-2">
                  <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Bank reference (UTR / cheque #)</span>
                  <input
                    value={form.finance?.bank_reference || ""}
                    onChange={(e) => setFinance("bank_reference", e.target.value)}
                    data-testid="admin-edit-bank-ref"
                    placeholder="UTR or cheque number when funds hit our bank"
                    className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono"
                  />
                </label>
              </div>
            </div>
          )}

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Notes</span>
            <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="admin-edit-notes" className="mt-1 w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
          </label>

          {/* Per-tender editor — this is the whole point of this dialog: fix
              a wrong method on ONE receipt without touching the others. */}
          {(form.payment_lines || []).length > 0 && (
            <div className="border border-canvas-line">
              <div className="px-3 py-2 border-b border-canvas-line bg-canvas-panel/50">
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                  Payment Tenders ({form.payment_lines.length}) — edit one line at a time
                </span>
              </div>
              <div className="divide-y divide-canvas-line">
                {form.payment_lines.map((line, idx) => {
                  const orig = sale?.payment_lines?.[idx] || {};
                  const dirty = ["method", "amount", "reference", "paid_on"].some(
                    (k) => (orig[k] ?? "") !== (line[k] ?? "")
                  );
                  return (
                    <div
                      key={idx}
                      data-testid={`admin-edit-tender-${idx}`}
                      className={`p-3 grid grid-cols-12 gap-2 items-end ${dirty ? "bg-amber-50" : ""}`}
                    >
                      <label className="col-span-3 block">
                        <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Method</span>
                        <select
                          value={(line.method || "cash").toLowerCase()}
                          onChange={(e) => setLine(idx, "method", e.target.value)}
                          data-testid={`admin-edit-tender-method-${idx}`}
                          className={`mt-1 w-full px-2 py-1.5 bg-canvas border text-sm uppercase ${dirty ? "border-amber-500 font-bold" : "border-canvas-line"}`}
                        >
                          {PAYMENT_METHODS.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                        </select>
                      </label>
                      <label className="col-span-2 block">
                        <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Amount ₹</span>
                        <input
                          type="number"
                          step="0.01"
                          value={line.amount ?? ""}
                          onChange={(e) => setLine(idx, "amount", parseFloat(e.target.value) || 0)}
                          data-testid={`admin-edit-tender-amt-${idx}`}
                          className="mt-1 w-full px-2 py-1.5 bg-canvas border border-canvas-line text-sm tabular-nums"
                        />
                      </label>
                      <label className="col-span-4 block">
                        <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Reference / UTR</span>
                        <input
                          value={line.reference || ""}
                          onChange={(e) => setLine(idx, "reference", e.target.value)}
                          data-testid={`admin-edit-tender-ref-${idx}`}
                          placeholder="e.g. UTR 402002003637"
                          className="mt-1 w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs font-mono"
                        />
                      </label>
                      <label className="col-span-2 block">
                        <span className="text-[9px] uppercase tracking-widest text-ink-muted font-mono">Paid On</span>
                        <input
                          type="date"
                          value={(line.paid_on || "").slice(0, 10)}
                          onChange={(e) => setLine(idx, "paid_on", e.target.value)}
                          data-testid={`admin-edit-tender-date-${idx}`}
                          className="mt-1 w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs"
                        />
                      </label>
                      <div className="col-span-1 text-right">
                        <div className="font-mono text-[9px] text-ink-muted">Receipt</div>
                        <div className="font-mono text-[9px]">{(line.receipt_no || "").split("/").pop()}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-3 py-2 border-t border-canvas-line bg-canvas-panel/30 text-[10px] font-mono text-ink-muted">
                Editing a line updates the sale row AND the cashbook (they share the same source).
                Receipt numbers are preserved.
              </div>
            </div>
          )}

          {/* Live diff preview */}
          {Object.keys(changed).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono mb-2">Changes to save ({Object.keys(changed).length})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.keys(changed).map((k) => (
                  <DiffPill key={k} change={{ field: k, before: sale[k], after: changed[k] }} />
                ))}
              </div>
            </div>
          )}

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Reason for edit *</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="admin-edit-reason"
              placeholder="e.g. Cashier keyed CASH instead of UPI; UTR verified with bank statement."
              className="mt-1 w-full px-3 py-2 bg-canvas border border-amber-500 text-sm"
              required
            />
            <div className="mt-1 font-mono text-[10px] text-ink-muted">Recorded on the audit trail — becomes visible to reviewers.</div>
          </label>

          {history.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ClockCounterClockwise size={13} className="text-ink-muted" />
                <span className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Edit history ({history.length})</span>
              </div>
              <ul className="border border-canvas-line divide-y divide-canvas-line max-h-56 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="p-3 text-xs" data-testid={`admin-edit-history-${h.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] text-ink-muted">{fmtDate(h.at)}</span>
                      <span className="font-semibold">{h.actor_email}</span>
                    </div>
                    <div className="mt-1 text-[11px] italic text-ink-muted">&ldquo;{h.reason}&rdquo;</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(h.changes || []).map((c, i) => (
                        <span key={i} className="inline-block bg-canvas-panel border border-canvas-line px-1.5 py-0.5 font-mono text-[9px]">
                          {c.field}
                        </span>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-canvas-line px-6 py-3 flex items-center justify-end gap-2 sticky bottom-0 bg-canvas">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-canvas-line">Cancel</button>
          <button
            type="submit"
            disabled={saving || Object.keys(changed).length === 0 || !reason.trim()}
            data-testid="admin-edit-save"
            className="px-4 py-2 bg-ink text-white text-xs font-semibold uppercase tracking-wider hover:bg-brand disabled:opacity-40"
          >
            {saving ? "Saving…" : `Save ${Object.keys(changed).length} change${Object.keys(changed).length === 1 ? "" : "s"}`}
          </button>
        </div>
      </form>
    </div>
  );
}
